<template>
  <div class="tile is-ancestor is-parent">
    <!-- Left Column: Basic Country Info -->
    <div class="tile is-vertical is-half">
      <div class="tile is-parent">
        <article class="tile is-child box">
          <p class="title">Country Information</p>

          <div class="field">
            <label class="label">Country Name</label>
            <div class="control">
              <input class="input"
                v-model="value.countryDesc">
              <span class="help is-danger"
                v-if="$v.entity && !$v.entity.countryDesc.required">Country Name is required</span>
            </div>
          </div>

          <div class="field">
            <label class="label">Country Code</label>
            <div class="control">
              <input class="input"
                v-model="value.countryCode"
                maxlength="2"
                :class="{ 'is-danger': $v.entity && $v.entity.countryCode && $v.entity.countryCode.$error }">
            </div>
            <span class="help is-danger"
              v-if="$v.entity && !$v.entity.countryCode.required">Country Code is required</span>
            <span class="help is-danger"
              v-if="$v.entity && $v.entity.countryCode && !$v.entity.countryCode.countryCodeUnique">Country Code already exists</span>
          </div>
          <div class="field">
            <label class="label">Calling Code</label>
            <div class="control">
              <input class="input"
                v-model="value.callingCode">
            </div>
            <span class="help is-danger"
              v-if="$v.entity && !$v.entity.callingCode.required">Calling Code is required</span>
          </div>

          <div class="field">
            <label class="label">Nationality</label>
            <div class="control">
              <input class="input"
                v-model="value.nationality">
            </div>
            <span class="help is-danger"
              v-if="$v.entity && !$v.entity.nationality.required">Nationality is required</span>
          </div>
          <div class="field">
            <label class="label">GST %</label>
            <div class="field has-addons">
              <div class="control">
                <div class="pretty p-default p-round p-smooth">
                  <input type="radio"
                    v-model="value.isGST"
                    :value="true">
                  <div class="state p-primary">
                    <label>Yes</label>
                  </div>
                </div>
                <div class="pretty p-default p-round p-smooth"
                  @change="value.gstRate = 0">
                  <input type="radio"
                    v-model="value.isGST"
                    :value="false">
                  <div class="state p-primary">
                    <label>No</label>
                  </div>
                </div>
              </div>
              <div class="control"
                style="max-width: 120px;">
                <input class="input"
                  type="number"
                  v-model.number="value.gstRate"
                  step="0.01"
                  placeholder="0.00"
                  :disabled="!value.isGST">
                <span class="help is-danger"
                  v-if="$v.entity && !$v.entity.gstRate.required">GST % is required</span>
                <span class="help is-danger"
                  v-if="$v.entity && $v.entity.gstRate && !$v.entity.gstRate.gstRateGreaterThanZero">GST % must be greater than 0</span>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">With-Hold Tax %</label>
            <div class="field has-addons">
              <div class="control">
                <div class="pretty p-default p-round p-smooth">
                  <input type="radio"
                    v-model="value.isWithHold"
                    :value="true">
                  <div class="state p-primary">
                    <label>Yes</label>
                  </div>
                </div>
                <div class="pretty p-default p-round p-smooth"
                  @change="value.withHoldTax = 0">
                  <input type="radio"
                    v-model="value.isWithHold"
                    :value="false">
                  <div class="state p-primary">
                    <label>No</label>
                  </div>
                </div>
              </div>
              <div class="control"
                style="max-width: 120px;">
                <input class="input"
                  type="number"
                  v-model.number="value.withHoldTax"
                  step="0.01"
                  placeholder="0.00"
                  :disabled="!value.isWithHold">
                <span class="help is-danger"
                  v-if="$v.entity && !$v.entity.withHoldTax.required">With-Hold Tax % is required</span>
                <!-- <span class="help is-danger"
                  v-if="$v.entity && $v.entity.withHoldTax && !$v.entity.withHoldTax.withHoldTaxGreaterThanZero">With-Hold Tax % must be greater than 0</span> -->
              </div>
            </div>
          </div>

          <div class="is-divider" />
          <div class="field">
            <div class="buttons has-addons is-centered">
              <span class="button"
                :class="{ 'is-success is-selected' : !value.deleted }"
                @click="toggleActive(false)">
                <span class="icon is-small">
                  <i class="mdi mdi-check" />
                </span>
                <span>Active</span>
              </span>
              <span class="button"
                :class="{ 'is-danger is-selected' : value.deleted }"
                @click="toggleActive(true)">
                <span class="icon is-small">
                  <i class="mdi mdi-close" />
                </span>
                <span>In-Active</span>
              </span>
            </div>
          </div>
        </article>
      </div>
      <!-- Accounting Periods Section -->
      <div class="tile is-parent">
        <article class="tile is-child box">
          <p class="title">Accounting Periods</p>

          <div class="field">
            <div class="control">
              <input class="input"
                v-model="value.accPeriod1"
                placeholder="Period 1">
            </div>
          </div>
          <div class="field">
            <div class="control">
              <input class="input"
                v-model="value.accPeriod2"
                placeholder="Period 2">
            </div>
          </div>
          <div class="field">
            <div class="control">
              <input class="input"
                v-model="value.accPeriod3"
                placeholder="Period 3">
            </div>
          </div>
          <div class="field">
            <div class="control">
              <input class="input"
                v-model="value.accPeriod4"
                placeholder="Period 4">
            </div>
          </div>
        </article>
      </div>

    </div>

    <!-- Right Column: Settings & Configurations -->
    <div class="tile is-vertical is-half">
      <!-- Leave Code Section -->
      <div class="tile is-parent">
        <article class="tile is-child box">
          <p class="title">Leave Code</p>

          <div class="field">
            <label class="label">Annual Leave</label>
            <div class="control">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
                <input type="checkbox"
                  v-model="value.alWait">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Initial Waiting Period</label>
                </div>
              </div>
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger ml-2">
                <input type="checkbox"
                  v-model="value.alReset">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Reset After Cycle</label>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Long Service Leave</label>
            <div class="control">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
                <input type="checkbox"
                  v-model="value.lslReset">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Reset After Cycle</label>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Rostered Day Off</label>
            <div class="control">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
                <input type="checkbox"
                  v-model="value.rdoAll">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>All</label>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Public Holidays</label>
            <div class="control">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
                <input type="checkbox"
                  v-model="value.phDays">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Number of Days</label>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
      <!-- Time Sheet Section -->
      <div class="tile is-parent">
        <article class="tile is-child box">
          <p class="title">Time Sheet</p>

          <div class="field">
            <label class="label">Employee Super Contr.</label>
            <div class="control">
              <div class="pretty p-default p-round p-smooth">
                <input type="radio"
                  v-model="value.tsEmpSuper"
                  :value="true">
                <div class="state p-primary">
                  <label>Yes</label>
                </div>
              </div>
              <div class="pretty p-default p-round p-smooth">
                <input type="radio"
                  v-model="value.tsEmpSuper"
                  :value="false">
                <div class="state p-primary">
                  <label>No</label>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Leave Enquiry Section -->
      <div class="tile is-parent">
        <article class="tile is-child box">
          <p class="title">Leave Enquiry</p>

          <div class="field">
            <label class="label">Annual Leave</label>
            <div class="control">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
                <input type="checkbox"
                  v-model="value.leAnnual">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Pro-Rata Hours up to Last Pay</label>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Rostered Day Off</label>
            <div class="control">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
                <input type="checkbox"
                  v-model="value.lerdoEntitlement">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Entitlement Hours up to Last Pay</label>
                </div>
              </div>
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger ml-2">
                <input type="checkbox"
                  v-model="value.lerdoProRata">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Pro-Rata Hours up to Last Pay</label>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Public Holidays</label>
            <div class="control">
              <div class="pretty p-icon p-curve p-smooth m-0 p-bigger">
                <input type="checkbox"
                  v-model="value.leph">
                <div class="state p-primary">
                  <span class="icon mso">check</span>
                  <label>Entitlement Hours up to Last pay</label>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Employee Section -->
      <div class="tile is-parent">
        <article class="tile is-child box">
          <p class="title">Employee</p>

          <div class="field">
            <label class="label">SuperSalSacrifice</label>
            <div class="control">
              <div class="pretty p-default p-round p-smooth">
                <input type="radio"
                  v-model="value.eSuperSalSac"
                  :value="true">
                <div class="state p-primary">
                  <label>Yes</label>
                </div>
              </div>
              <div class="pretty p-default p-round p-smooth">
                <input type="radio"
                  v-model="value.eSuperSalSac"
                  :value="false">
                <div class="state p-primary">
                  <label>No</label>
                </div>
              </div>
            </div>
          </div>

          <div class="field">
            <label class="label">Super Override Value & Percentage</label>
            <div class="field has-addons">
              <div class="control">
                <input class="input"
                  type="number"
                  v-model.number="value.eSuperOver"
                  step="0.01"
                  placeholder="0.00">
              </div>
              <div class="control">
                <input class="input"
                  type="number"
                  v-model.number="value.eSuperOverPer"
                  step="0.01"
                  placeholder="0.00">
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Previous Close Date Section -->
      <div class="tile is-parent">
        <article class="tile is-child box">
          <p class="title">Previous Close Date</p>

          <div class="field">
            <label class="label">Previous Close Date</label>
            <v-date-picker v-model="selectedPrevClosingDate"
              :is-dark="$theme.isDark"
              :masks="formats"
              :attributes="attrs"
              :locale="$userInfo.locale"
              :timezone="$filters.getIanaTimezone()">
              <template v-slot="{ inputValue, inputEvents }">
                <div class="field">
                  <div class="control has-icons-left">
                    <input type="text"
                      class="input"
                      placeholder="Previous Close Date"
                      :value="inputValue"
                      v-on="inputEvents"
                      readonly>
                    <span class="icon is-small is-left">
                      <i class="mdi mdi-calendar mdi-18px" />
                    </span>
                  </div>
                </div>
              </template>
            </v-date-picker>
          </div>

          <div class="field">
            <label class="label">Parts Present As Repair Enabled</label>
            <div class="control">
              <div class="pretty p-default p-round p-smooth">
                <input type="radio"
                  v-model="value.partsPresentAsRepairEnabled"
                  :value="true">
                <div class="state p-primary">
                  <label>Yes</label>
                </div>
              </div>
              <div class="pretty p-default p-round p-smooth">
                <input type="radio"
                  v-model="value.partsPresentAsRepairEnabled"
                  :value="false">
                <div class="state p-primary">
                  <label>No</label>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import StoreMixin from './storeMixin'
import { DateTimeFiltersMixin } from '@/components/mixins/filters'

export default {
  name: 'CountrySettingDetail',
  inject: ['$vv'],
  mixins: [StoreMixin, DateTimeFiltersMixin],
  props: {
    value: Object,
    isNew: Boolean
  },
  data() {
    return {
      selectedPrevClosingDate: null,
      formats: {
        title: 'MMMM YYYY',
        weekdays: 'W',
        navMonths: 'MMM',
        input: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
        data: ['L', 'DD-MM-YYYY', 'DD/MM/YYYY'],
        dayPopover: 'L'
      },
      attrs: [
        {
          key: 'today',
          highlight: {
            backgroundColor: '#ff8080'
          },
          popover: {
            label: 'Today'
          },
          dates: new Date()
        }
      ],
      modelConfig: {
        start: {
          timeAdjust: '08:00:00'
        },
        end: {
          timeAdjust: '08:00:00'
        }
      }
    }
  },
  computed: {
    $v() {
      return this.$vv || this.$v
    }
  },
  watch: {
    value: {
      handler: function (newVal) {
        this.$emit('input', newVal)
      },
      deep: true
    },
    selectedPrevClosingDate: function (newVal) {
      if (newVal) {
        this.value.prevClosingDate = newVal.toISOString().split('.')[0] + 'Z'
      } else {
        this.value.prevClosingDate = null
      }
    }
  },
  created() {
    if (this.value && this.value.prevClosingDate) {
      this.selectedPrevClosingDate = this.value.prevClosingDate ? new Date(`${this.value.prevClosingDate}`) : null
    }
  },
  mounted() {
    if (this.$v && this.$v.detailGroup) {
      this.$v.detailGroup.$touch()
    }
  },
  methods: {
    toggleActive(deleted) {
      this.value.deleted = deleted
    }
  }
}
</script>
<style lang="scss" scoped>
.is-divider {
  margin: 1.2em 0;
}
</style>
